import { BaseService, createApiResponse } from './BaseService';

export class TicketService extends BaseService {

  async getTickets(filters = {}) {
    await this.simulateDelay();
    
    const cacheKey = 'tickets';
    const cached = this.getCached(cacheKey);
    
    if (cached) {
      // Merge with localStorage tickets
      const storedTickets = this.getStoredTickets();
      const allTickets = [...storedTickets, ...cached.filter(t => !storedTickets.find(st => st.id === t.id))];
      return createApiResponse(allTickets, 'Tickets retrieved from cache');
    }
    
    // Get stored tickets from localStorage
    const storedTickets = this.getStoredTickets();
    
    const defaultTickets = [
      {
        id: 'TKT-001',
        title: 'Login issues with new system',
        description: 'Unable to log into the new customer portal',
        status: 'open',
        priority: 'high',
        category: 'technical',
        department: 'IT',
        assignedTo: 'agent_1',
        createdBy: 'customer_1',
        createdAt: '2024-01-20T09:00:00Z',
        updatedAt: '2024-01-20T10:30:00Z',
        dueDate: '2024-01-22T17:00:00Z',
        tags: ['login', 'portal', 'urgent'],
        attachments: [],
        comments: [
          {
            id: 'comment_1',
            author: 'customer_1',
            content: 'This is preventing me from accessing my account',
            createdAt: '2024-01-20T09:00:00Z'
          }
        ],
        sla: {
          responseTime: 2,
          resolutionTime: 24,
          status: 'on_track'
        },
        communications: [
          {
            id: 'comm_tkt001_1',
            author: {
              name: 'Customer 1',
              type: 'customer'
            },
            type: 'comment',
            content: 'This is preventing me from accessing my account',
            createdAt: '2024-01-20T09:00:00Z',
            isInternal: false
          },
          {
            id: 'comm_tkt001_2',
            author: {
              name: 'Agent 1',
              type: 'agent'
            },
            type: 'note',
            content: 'INTERNAL NOTE: Customer appears to be using outdated browser (IE11). May need to guide them to upgrade. Checking with dev team if this is a known compatibility issue.',
            createdAt: '2024-01-20T09:20:00Z',
            isInternal: true
          },
          {
            id: 'comm_tkt001_3',
            author: {
              name: 'Agent 1',
              type: 'agent'
            },
            type: 'comment',
            content: 'I\'m investigating this issue. Can you provide your username?',
            createdAt: '2024-01-20T09:30:00Z',
            isInternal: false
          }
        ],
        timeTracking: [
          {
            id: 'time_tkt001_1',
            agent: {
              id: 'agent_1',
              name: 'Agent 1'
            },
            description: 'Initial review and diagnosis',
            duration: 20,
            startTime: '2024-01-20T09:15:00Z',
            endTime: '2024-01-20T09:35:00Z'
          },
          {
            id: 'time_tkt001_2',
            agent: {
              id: 'agent_1',
              name: 'Agent 1'
            },
            description: 'Testing portal access',
            duration: 30,
            startTime: '2024-01-20T10:00:00Z',
            endTime: '2024-01-20T10:30:00Z'
          }
        ]
      },
      {
        id: 'TKT-002',
        title: 'Billing inquiry',
        description: 'Question about monthly charges',
        status: 'in_progress',
        priority: 'medium',
        category: 'billing',
        department: 'Finance',
        assignedTo: 'agent_2',
        createdBy: 'customer_2',
        createdAt: '2024-01-19T14:30:00Z',
        updatedAt: '2024-01-20T08:45:00Z',
        dueDate: '2024-01-23T17:00:00Z',
        tags: ['billing', 'charges'],
        attachments: [],
        comments: [
          {
            id: 'comment_2',
            author: 'customer_2',
            content: 'I see an unexpected charge on my bill',
            createdAt: '2024-01-19T14:30:00Z'
          },
          {
            id: 'comment_3',
            author: 'agent_2',
            content: 'I\'m looking into this for you',
            createdAt: '2024-01-20T08:45:00Z'
          }
        ],
        sla: {
          responseTime: 4,
          resolutionTime: 48,
          status: 'on_track'
        }
      },
      {
        id: 'TKT-003',
        title: 'Software installation request',
        description: 'Need Adobe Creative Suite installed on my workstation',
        status: 'open',
        priority: 'low',
        category: 'software',
        department: 'IT',
        assignedTo: 'agent_1',
        createdBy: 'customer_3',
        createdAt: '2024-01-20T11:00:00Z',
        updatedAt: '2024-01-20T11:00:00Z',
        dueDate: '2024-01-25T17:00:00Z',
        tags: ['software', 'installation'],
        attachments: [],
        comments: [
          {
            id: 'comment_4',
            author: 'customer_3',
            content: 'This is for a new project starting next week',
            createdAt: '2024-01-20T11:00:00Z'
          }
        ],
        sla: {
          responseTime: 8,
          resolutionTime: 72,
          status: 'on-track'
        }
      },
      {
        id: 'TKT-004',
        title: 'Password reset required',
        description: 'Unable to access email account, need password reset',
        status: 'resolved',
        priority: 'high',
        category: 'access',
        department: 'IT',
        assignedTo: 'agent_1',
        createdBy: 'customer_4',
        createdAt: '2024-01-19T08:00:00Z',
        updatedAt: '2024-01-19T09:15:00Z',
        dueDate: '2024-01-19T12:00:00Z',
        tags: ['password', 'email', 'access'],
        attachments: [],
        comments: [
          {
            id: 'comment_5',
            author: 'customer_4',
            content: 'Urgent - I need to access important emails',
            createdAt: '2024-01-19T08:00:00Z'
          },
          {
            id: 'comment_6',
            author: 'agent_1',
            content: 'Password has been reset. Please check your personal email for the temporary password.',
            createdAt: '2024-01-19T09:15:00Z'
          }
        ],
        sla: {
          responseTime: 1,
          resolutionTime: 4,
          status: 'on-track'
        }
      },
      {
        id: 'TKT-005',
        title: 'Network connectivity issues',
        description: 'Intermittent connection drops in Building A, 3rd floor',
        status: 'open',
        priority: 'critical',
        category: 'network',
        department: 'IT',
        assignedTo: 'agent_2',
        createdBy: 'customer_5',
        createdAt: '2024-01-20T13:45:00Z',
        updatedAt: '2024-01-20T14:00:00Z',
        dueDate: '2024-01-20T18:00:00Z',
        tags: ['network', 'critical', 'connectivity'],
        attachments: [
          {
            id: 'att_1',
            name: 'network_diagnostics.log',
            size: 45120,
            type: 'text/plain',
            url: '/files/network_diagnostics.log',
            uploadedAt: '2024-01-20T13:50:00Z'
          }
        ],
        comments: [
          {
            id: 'comment_7',
            author: 'customer_5',
            content: 'Affecting multiple users in the area. Work is being disrupted.',
            createdAt: '2024-01-20T13:45:00Z'
          },
          {
            id: 'comment_8',
            author: 'agent_2',
            content: 'Dispatching network team to investigate the issue.',
            createdAt: '2024-01-20T14:00:00Z'
          }
        ],
        sla: {
          responseTime: 1,
          resolutionTime: 4,
          status: 'warning'
        },
        communications: [
          {
            id: 'comm_1',
            author: {
              name: 'customer_5',
              type: 'customer'
            },
            type: 'comment',
            content: 'Affecting multiple users in the area. Work is being disrupted.',
            createdAt: '2024-01-20T13:45:00Z',
            isInternal: false
          },
          {
            id: 'comm_tkt005_internal',
            author: {
              name: 'agent_2',
              type: 'agent'
            },
            type: 'note',
            content: 'INTERNAL: Likely switch failure on 3rd floor. Escalating to L2 network team. Priority critical due to multiple users affected. Building manager contacted for access to network room.',
            createdAt: '2024-01-20T13:55:00Z',
            isInternal: true
          },
          {
            id: 'comm_2',
            author: {
              name: 'agent_2',
              type: 'agent'
            },
            type: 'comment',
            content: 'Dispatching network team to investigate the issue.',
            createdAt: '2024-01-20T14:00:00Z',
            isInternal: false
          }
        ],
        timeTracking: [
          {
            id: 'time_1',
            agent: {
              id: 'agent_2',
              name: 'Agent 2'
            },
            description: 'Initial investigation',
            duration: 15,
            startTime: '2024-01-20T14:00:00Z',
            endTime: '2024-01-20T14:15:00Z'
          }
        ]
      },
      {
        id: 'TKT-006',
        title: 'Printer not working',
        description: 'Conference room B printer showing error message',
        status: 'pending',
        priority: 'medium',
        category: 'hardware',
        department: 'IT',
        assignedTo: 'agent_3',
        createdBy: 'customer_6',
        createdAt: '2024-01-20T10:30:00Z',
        updatedAt: '2024-01-20T12:00:00Z',
        dueDate: '2024-01-21T17:00:00Z',
        tags: ['hardware', 'printer'],
        attachments: [
          {
            id: 'att_2',
            name: 'error_photo.jpg',
            size: 125840,
            type: 'image/jpeg',
            url: '/files/error_photo.jpg',
            uploadedAt: '2024-01-20T10:35:00Z'
          }
        ],
        comments: [
          {
            id: 'comment_9',
            author: 'customer_6',
            content: 'Error code E-10-43 shown on display',
            createdAt: '2024-01-20T10:30:00Z'
          },
          {
            id: 'comment_10',
            author: 'agent_3',
            content: 'Ordered replacement toner cartridge. Should arrive tomorrow.',
            createdAt: '2024-01-20T12:00:00Z'
          }
        ],
        sla: {
          responseTime: 4,
          resolutionTime: 24,
          status: 'on-track'
        }
      },
      {
        id: 'TKT-042',
        title: 'VPN connection timeout',
        description: 'Cannot connect to company VPN from remote location',
        status: 'pending',
        priority: 'high',
        category: 'network',
        department: 'IT',
        assignedTo: 'agent_1',
        createdBy: 'customer_7',
        createdAt: '2024-01-20T15:00:00Z',
        updatedAt: '2024-01-20T16:30:00Z',
        dueDate: '2024-01-21T12:00:00Z',
        tags: ['vpn', 'remote', 'connectivity'],
        attachments: [],
        comments: [
          {
            id: 'comment_11',
            author: 'customer_7',
            content: 'Getting timeout error after authentication',
            createdAt: '2024-01-20T15:00:00Z'
          },
          {
            id: 'comment_12',
            author: 'customer_7',
            content: 'This is urgent as I need to access internal resources',
            createdAt: '2024-01-20T16:30:00Z'
          }
        ],
        sla: {
          responseTime: 2,
          resolutionTime: 8,
          status: 'on-track'
        },
        communications: [
          {
            id: 'comm_tkt042_1',
            author: {
              name: 'customer_7',
              type: 'customer'
            },
            type: 'comment',
            content: 'Getting timeout error after authentication',
            createdAt: '2024-01-20T15:00:00Z',
            isInternal: false
          }
        ],
        timeTracking: []
      },
      {
        id: 'TKT-043',
        title: 'Database backup failure',
        description: 'Automated backup job failed last night',
        status: 'in_progress',
        priority: 'critical',
        category: 'infrastructure',
        department: 'IT',
        assignedTo: 'agent_2',
        createdBy: 'system',
        createdAt: '2024-01-20T08:00:00Z',
        updatedAt: '2024-01-20T14:00:00Z',
        dueDate: '2024-01-20T20:00:00Z',
        tags: ['database', 'backup', 'critical'],
        attachments: [],
        comments: [
          {
            id: 'comment_13',
            author: 'system',
            content: 'Backup job DB_PROD_DAILY failed with error code 1042',
            createdAt: '2024-01-20T08:00:00Z'
          },
          {
            id: 'comment_14',
            author: 'agent_2',
            content: 'Investigating disk space and permissions',
            createdAt: '2024-01-20T14:00:00Z'
          }
        ],
        sla: {
          responseTime: 1,
          resolutionTime: 4,
          status: 'at-risk'
        },
        communications: [
          {
            id: 'comm_tkt043_1',
            author: {
              name: 'System Monitor',
              type: 'system'
            },
            type: 'alert',
            content: 'Backup job DB_PROD_DAILY failed with error code 1042',
            createdAt: '2024-01-20T08:00:00Z',
            isInternal: false
          },
          {
            id: 'comm_tkt043_internal',
            author: {
              name: 'agent_2',
              type: 'agent'
            },
            type: 'note',
            content: 'INTERNAL: Disk usage at 95%. Need to clear old logs before retry.',
            createdAt: '2024-01-20T13:00:00Z',
            isInternal: true
          }
        ],
        timeTracking: [
          {
            id: 'time_tkt043_1',
            agent: {
              id: 'agent_2',
              name: 'Agent 2'
            },
            description: 'Investigating backup failure',
            duration: 45,
            startTime: '2024-01-20T09:00:00Z',
            endTime: '2024-01-20T09:45:00Z'
          }
        ]
      },
      {
        id: 'TKT-045',
        title: 'Mobile app crashing on login',
        description: 'iOS app crashes when attempting to log in',
        status: 'resolved',
        priority: 'high',
        category: 'software',
        department: 'IT',
        assignedTo: 'agent_1',
        createdBy: 'customer_8',
        createdAt: '2024-01-19T10:00:00Z',
        updatedAt: '2024-01-20T11:30:00Z',
        dueDate: '2024-01-20T10:00:00Z',
        tags: ['mobile', 'ios', 'login'],
        attachments: [],
        comments: [
          {
            id: 'comment_15',
            author: 'customer_8',
            content: 'App crashes immediately after entering credentials',
            createdAt: '2024-01-19T10:00:00Z'
          },
          {
            id: 'comment_16',
            author: 'agent_1',
            content: 'Fixed in version 2.1.3. Please update your app.',
            createdAt: '2024-01-20T11:30:00Z'
          }
        ],
        sla: {
          responseTime: 1,
          resolutionTime: 24,
          status: 'completed'
        },
        communications: [
          {
            id: 'comm_tkt045_1',
            author: {
              name: 'customer_8',
              type: 'customer'
            },
            type: 'comment',
            content: 'App crashes immediately after entering credentials',
            createdAt: '2024-01-19T10:00:00Z',
            isInternal: false
          },
          {
            id: 'comm_tkt045_2',
            author: {
              name: 'agent_1',
              type: 'agent'
            },
            type: 'comment',
            content: 'Fixed in version 2.1.3. Please update your app from the App Store.',
            createdAt: '2024-01-20T11:30:00Z',
            isInternal: false
          }
        ],
        timeTracking: [
          {
            id: 'time_tkt045_1',
            agent: {
              id: 'agent_1',
              name: 'Agent 1'
            },
            description: 'Reproduced issue and coordinated with dev team',
            duration: 90,
            startTime: '2024-01-19T14:00:00Z',
            endTime: '2024-01-19T15:30:00Z'
          }
        ]
      },
      {
        id: 'TKT-046',
        title: 'Email notifications not received',
        description: 'Customer reports not receiving ticket update emails',
        status: 'open',
        priority: 'medium',
        category: 'technical',
        department: 'IT',
        assignedTo: 'agent_1',
        createdBy: 'customer_9',
        createdAt: '2024-01-20T09:15:00Z',
        updatedAt: '2024-01-20T09:15:00Z',
        dueDate: '2024-01-22T17:00:00Z',
        tags: ['email', 'notifications'],
        attachments: [],
        comments: [
          {
            id: 'comment_17',
            author: 'customer_9',
            content: 'I have not received any email notifications for the past week',
            createdAt: '2024-01-20T09:15:00Z'
          }
        ],
        sla: {
          responseTime: 4,
          resolutionTime: 48,
          status: 'on-track'
        },
        communications: [
          {
            id: 'comm_tkt046_1',
            author: {
              name: 'customer_9',
              type: 'customer'
            },
            type: 'comment',
            content: 'I have not received any email notifications for the past week',
            createdAt: '2024-01-20T09:15:00Z',
            isInternal: false
          }
        ],
        timeTracking: []
      }
    ];
    
    // Merge default tickets with stored tickets
    const allTickets = [...storedTickets, ...defaultTickets.filter(t => !storedTickets.find(st => st.id === t.id))];
    
    this.setCache(cacheKey, allTickets);
    return createApiResponse(allTickets, 'Tickets retrieved successfully');
  }

  // Helper method to get tickets from sessionStorage
  getStoredTickets() {
    try {
      const stored = sessionStorage.getItem('user_tickets');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  // Helper method to save tickets to sessionStorage
  saveStoredTickets(tickets) {
    try {
      sessionStorage.setItem('user_tickets', JSON.stringify(tickets));
    } catch (err) {
      console.error('Failed to save tickets to sessionStorage:', err);
    }
  }

  async getTicket(id) {
    await this.simulateDelay();
    
    const ticketsResponse = await this.getTickets();
    const ticket = ticketsResponse.data.find(t => t.id === id);
    
    if (!ticket) {
      return createApiResponse(null, 'Ticket not found', false);
    }
    
    return createApiResponse(ticket, 'Ticket retrieved successfully');
  }

  async createTicket(ticketData) {
    await this.simulateDelay();
    
    // Get current user info
    const userId = sessionStorage.getItem('userId') || 'current_user';
    
    const newTicket = {
      id: `TKT-${String(Date.now()).slice(-6)}`,
      title: ticketData.subject || ticketData.title || 'Untitled Ticket',
      subject: ticketData.subject || ticketData.title || 'Untitled Ticket',
      description: ticketData.description || '',
      priority: ticketData.priority || 'medium',
      status: 'open',
      category: ticketData.category || 'general',
      createdBy: userId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      comments: [],
      attachments: ticketData.attachments || [],
      tags: ticketData.tags || [],
      sla: {
        responseTime: 2,
        resolutionTime: 24,
        status: 'on_track'
      }
    };
    
    // Save to localStorage
    const storedTickets = this.getStoredTickets();
    storedTickets.unshift(newTicket); // Add to beginning
    this.saveStoredTickets(storedTickets);
    
    // Clear cache to force refresh
    this.clearCache();
    
    return createApiResponse(newTicket, 'Ticket created successfully');
  }

  async updateTicket(id, ticketData) {
    await this.simulateDelay();
    
    const updatedTicket = {
      ...ticketData,
      id,
      updatedAt: new Date().toISOString()
    };
    
    // Clear cache to force refresh
    this.clearCache();
    
    return createApiResponse(updatedTicket, 'Ticket updated successfully');
  }

  async deleteTicket(id) {
    await this.simulateDelay();
    
    // Clear cache to force refresh
    this.clearCache();
    
    return createApiResponse({ id }, 'Ticket deleted successfully');
  }

  async getTicketTypes() {
    await this.simulateDelay();
    
    const cacheKey = 'ticketTypes';
    const cached = this.getCached(cacheKey);
    
    if (cached) {
      return createApiResponse(cached, 'Ticket types retrieved from cache');
    }
    
    const ticketTypes = [
      {
        id: 'type_1',
        name: 'Technical Support',
        description: 'Technical issues and troubleshooting',
        category: 'technical',
        workflow: 'standard',
        sla: {
          responseTime: 2,
          resolutionTime: 24
        },
        fields: [
          { name: 'priority', type: 'select', required: true },
          { name: 'urgency', type: 'select', required: false },
          { name: 'system', type: 'text', required: true }
        ],
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z'
      },
      {
        id: 'type_2',
        name: 'Billing Inquiry',
        description: 'Questions about billing and payments',
        category: 'billing',
        workflow: 'billing',
        sla: {
          responseTime: 4,
          resolutionTime: 48
        },
        fields: [
          { name: 'account_number', type: 'text', required: true },
          { name: 'invoice_number', type: 'text', required: false }
        ],
        isActive: true,
        createdAt: '2024-01-02T00:00:00Z'
      }
    ];
    
    this.setCache(cacheKey, ticketTypes);
    return createApiResponse(ticketTypes, 'Ticket types retrieved successfully');
  }

  async getPriorities() {
    await this.simulateDelay();
    
    const cacheKey = 'priorities';
    const cached = this.getCached(cacheKey);
    
    if (cached) {
      return createApiResponse(cached, 'Priorities retrieved from cache');
    }
    
    const priorities = [
      {
        id: 'priority_1',
        name: 'Critical',
        level: 1,
        color: '#dc2626',
        sla: {
          responseTime: 0.5,
          resolutionTime: 4
        },
        escalationRules: {
          autoEscalate: true,
          escalateAfter: 2
        },
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z'
      },
      {
        id: 'priority_2',
        name: 'High',
        level: 2,
        color: '#ea580c',
        sla: {
          responseTime: 1,
          resolutionTime: 8
        },
        escalationRules: {
          autoEscalate: true,
          escalateAfter: 4
        },
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z'
      },
      {
        id: 'priority_3',
        name: 'Medium',
        level: 3,
        color: '#d97706',
        sla: {
          responseTime: 2,
          resolutionTime: 24
        },
        escalationRules: {
          autoEscalate: false,
          escalateAfter: 12
        },
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z'
      },
      {
        id: 'priority_4',
        name: 'Low',
        level: 4,
        color: '#16a34a',
        sla: {
          responseTime: 4,
          resolutionTime: 72
        },
        escalationRules: {
          autoEscalate: false,
          escalateAfter: 24
        },
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z'
      }
    ];
    
    this.setCache(cacheKey, priorities);
    return createApiResponse(priorities, 'Priorities retrieved successfully');
  }

  async getCustomFields() {
    await this.simulateDelay();
    
    const cacheKey = 'customFields';
    const cached = this.getCached(cacheKey);
    
    if (cached) {
      return createApiResponse(cached, 'Custom fields retrieved from cache');
    }
    
    const customFields = [
      {
        id: 'field_1',
        name: 'System Version',
        type: 'text',
        description: 'Version of the system being used',
        required: false,
        validation: {
          pattern: '^\\d+\\.\\d+\\.\\d+$',
          message: 'Please enter a valid version number (e.g., 1.2.3)'
        },
        display: {
          position: 'top',
          width: 'half'
        },
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z'
      },
      {
        id: 'field_2',
        name: 'Business Impact',
        type: 'select',
        description: 'Impact on business operations',
        required: true,
        options: [
          { value: 'low', label: 'Low Impact' },
          { value: 'medium', label: 'Medium Impact' },
          { value: 'high', label: 'High Impact' },
          { value: 'critical', label: 'Critical Impact' }
        ],
        display: {
          position: 'top',
          width: 'half'
        },
        isActive: true,
        createdAt: '2024-01-02T00:00:00Z'
      }
    ];
    
    this.setCache(cacheKey, customFields);
    return createApiResponse(customFields, 'Custom fields retrieved successfully');
  }

  async getTicketStatuses() {
    await this.simulateDelay();
    
    const cacheKey = 'ticketStatuses';
    const cached = this.getCached(cacheKey);
    
    if (cached) {
      return createApiResponse(cached, 'Ticket statuses retrieved from cache');
    }
    
    const statuses = [
      {
        id: 'status_1',
        name: 'Open',
        description: 'Ticket is open and awaiting assignment',
        category: 'active',
        color: '#3b82f6',
        isDefault: true,
        transitions: ['in_progress', 'closed'],
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z'
      },
      {
        id: 'status_2',
        name: 'In Progress',
        description: 'Ticket is being worked on',
        category: 'active',
        color: '#f59e0b',
        isDefault: false,
        transitions: ['pending', 'resolved', 'closed'],
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z'
      },
      {
        id: 'status_3',
        name: 'Pending',
        description: 'Ticket is waiting for customer response',
        category: 'pending',
        color: '#8b5cf6',
        isDefault: false,
        transitions: ['in_progress', 'closed'],
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z'
      },
      {
        id: 'status_4',
        name: 'Resolved',
        description: 'Ticket has been resolved',
        category: 'resolved',
        color: '#10b981',
        isDefault: false,
        transitions: ['closed', 'reopened'],
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z'
      },
      {
        id: 'status_5',
        name: 'Closed',
        description: 'Ticket is closed',
        category: 'closed',
        color: '#6b7280',
        isDefault: false,
        transitions: ['reopened'],
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z'
      }
    ];
    
    this.setCache(cacheKey, statuses);
    return createApiResponse(statuses, 'Ticket statuses retrieved successfully');
  }

  async getTicketCategories() {
    await this.simulateDelay();
    
    const cacheKey = 'ticketCategories';
    const cached = this.getCached(cacheKey);
    
    if (cached) {
      return createApiResponse(cached, 'Ticket categories retrieved from cache');
    }
    
    const categories = [
      {
        id: 'cat_1',
        name: 'Technical Support',
        description: 'Technical issues and system problems',
        parentId: null,
        level: 0,
        path: '/technical-support',
        routing: {
          department: 'IT',
          defaultAssignee: 'agent_1'
        },
        permissions: {
          view: ['agent', 'admin'],
          edit: ['admin'],
          delete: ['admin']
        },
        analytics: {
          ticketCount: 156,
          avgResolutionTime: 2.5,
          customerSatisfaction: 4.2
        },
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z'
      },
      {
        id: 'cat_2',
        name: 'Billing',
        description: 'Billing inquiries and payment issues',
        parentId: null,
        level: 0,
        path: '/billing',
        routing: {
          department: 'Finance',
          defaultAssignee: 'agent_2'
        },
        permissions: {
          view: ['agent', 'admin'],
          edit: ['admin'],
          delete: ['admin']
        },
        analytics: {
          ticketCount: 89,
          avgResolutionTime: 1.8,
          customerSatisfaction: 4.5
        },
        isActive: true,
        createdAt: '2024-01-02T00:00:00Z'
      },
      {
        id: 'cat_3',
        name: 'Account Management',
        description: 'Account settings, password resets, and user management',
        parentId: null,
        level: 0,
        path: '/account-management',
        routing: {
          department: 'IT',
          defaultAssignee: 'agent_1'
        },
        permissions: {
          view: ['agent', 'admin'],
          edit: ['admin'],
          delete: ['admin']
        },
        analytics: {
          ticketCount: 234,
          avgResolutionTime: 1.5,
          customerSatisfaction: 4.3
        },
        isActive: true,
        createdAt: '2024-01-03T00:00:00Z'
      },
      {
        id: 'cat_4',
        name: 'Hardware',
        description: 'Hardware issues and equipment problems',
        parentId: null,
        level: 0,
        path: '/hardware',
        routing: {
          department: 'IT',
          defaultAssignee: 'agent_1'
        },
        permissions: {
          view: ['agent', 'admin'],
          edit: ['admin'],
          delete: ['admin']
        },
        analytics: {
          ticketCount: 112,
          avgResolutionTime: 3.2,
          customerSatisfaction: 4.0
        },
        isActive: true,
        createdAt: '2024-01-04T00:00:00Z'
      },
      {
        id: 'cat_5',
        name: 'Software',
        description: 'Software installation, updates, and compatibility issues',
        parentId: null,
        level: 0,
        path: '/software',
        routing: {
          department: 'IT',
          defaultAssignee: 'agent_1'
        },
        permissions: {
          view: ['agent', 'admin'],
          edit: ['admin'],
          delete: ['admin']
        },
        analytics: {
          ticketCount: 198,
          avgResolutionTime: 2.8,
          customerSatisfaction: 4.1
        },
        isActive: true,
        createdAt: '2024-01-05T00:00:00Z'
      }
    ];
    
    this.setCache(cacheKey, categories);
    return createApiResponse(categories, 'Ticket categories retrieved successfully');
  }

  async createTicketCategory(categoryData) {
    await this.simulateDelay();
    
    const newCategory = {
      id: `cat_${Date.now()}`,
      name: categoryData.name || '',
      description: categoryData.description || '',
      color: categoryData.color || '#3B82F6',
      parentId: categoryData.parentId || null,
      level: categoryData.level || 0,
      path: categoryData.path || `/${categoryData.name?.toLowerCase().replace(/\s+/g, '-')}`,
      routing: categoryData.routing || {
        department: categoryData.department || null,
        defaultAssignee: categoryData.defaultAssignee || null
      },
      permissions: categoryData.permissions || {
        view: ['agent', 'admin'],
        edit: ['admin'],
        delete: ['admin']
      },
      analytics: {
        ticketCount: 0,
        avgResolutionTime: 0,
        customerSatisfaction: 0
      },
      isActive: categoryData.isActive !== undefined ? categoryData.isActive : true,
      createdAt: new Date().toISOString()
    };
    
    // Clear cache to force refresh
    this.clearCache('ticketCategories');
    
    return createApiResponse(newCategory, 'Ticket category created successfully');
  }

  async updateTicketCategory(categoryId, categoryData) {
    await this.simulateDelay();
    
    const updatedCategory = {
      id: categoryId,
      ...categoryData,
      updatedAt: new Date().toISOString()
    };
    
    // Clear cache to force refresh
    this.clearCache('ticketCategories');
    
    return createApiResponse(updatedCategory, 'Ticket category updated successfully');
  }

  async deleteTicketCategory(categoryId) {
    await this.simulateDelay();
    
    // Clear cache to force refresh
    this.clearCache('ticketCategories');
    
    return createApiResponse({ id: categoryId }, 'Ticket category deleted successfully');
  }
}
